import { MobileCreateAction } from "../layout/MobileCreateAction";
import {
  CanAccess,
  InfiniteListBase,
  RecordContextProvider,
  useListContext,
} from "ra-core";
import { Link } from "react-router";
import { Plus, ChevronRight } from "lucide-react";
import { MobileListSearch } from "../layout/MobileListSearch";
import { CompanyListFilter } from "./CompanyListFilter";
import { TagFilterSummary } from "../misc/TagFilterSummary";
import { Button } from "@/components/ui/button";
import { InfinitePagination } from "../misc/InfinitePagination";
import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { CompanyAvatar } from "./CompanyAvatar";
import { CompanyTagsList } from "./CompanyTagsList";
import type { Company } from "../types";

export function MobileCompanyList() {
  return (
    <InfiniteListBase perPage={25} sort={{ field: "name", order: "ASC" }}>
      <CompanyCards />
    </InfiniteListBase>
  );
}

function CompanyCards() {
  const { data = [], isPending, error, refetch } = useListContext<Company>();
  return (
    <>
      <MobileHeader
        title="Компании"
        actions={
          <>
            <CanAccess resource="companies" action="create">
              <MobileCreateAction>
                <Button asChild size="icon" aria-label="Добавить компанию">
                  <Link to="/companies/create">
                    <Plus className="size-5" />
                  </Link>
                </Button>
              </MobileCreateAction>
            </CanAccess>
            <CompanyListFilter />
          </>
        }
      />
      <MobileContent>
        <MobileListSearch label="Поиск компаний" />
        <TagFilterSummary />
        {error ? (
          <div role="alert" className="rounded-xl border bg-card p-4">
            Не удалось загрузить компании.
            <Button
              variant="outline"
              className="mt-3"
              onClick={() => refetch()}
            >
              Повторить
            </Button>
          </div>
        ) : (
          <div className="space-y-3" aria-busy={isPending}>
            {data.map((company) => (
              <RecordContextProvider key={company.id} value={company}>
                <Link
                  to={`/companies/${company.id}/show`}
                  aria-label={`Открыть компанию ${company.name}`}
                  data-mobile-reveal
                  className="mobile-counterparty-card flex items-center gap-3 rounded-3xl border bg-card p-4 shadow-sm transition-colors hover:bg-accent"
                >
                  <div className="shrink-0">
                    <CompanyAvatar />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="break-words font-semibold">
                      {company.name}
                    </h2>
                    <div className="mt-2">
                      <CompanyTagsList limit={3} />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span>Контактов: {company.nb_contacts ?? 0}</span>
                      <span>Проектов: {company.nb_deals ?? 0}</span>
                    </div>
                  </div>
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                </Link>
              </RecordContextProvider>
            ))}
            {!isPending && !data.length && (
              <p className="rounded-xl border bg-card p-5 text-sm text-muted-foreground">
                Компании не найдены
              </p>
            )}
            {isPending && !data.length && (
              <p className="p-4 text-sm text-muted-foreground">
                Загрузка компаний…
              </p>
            )}
          </div>
        )}
        {!error && (
          <div className="mt-4 flex justify-center">
            <InfinitePagination />
          </div>
        )}
      </MobileContent>
    </>
  );
}
